import React, { useState, useEffect } from "react";
import { 
  TextField, 
  Button, 
  Snackbar, 
  Alert, 
  AlertColor, 
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from "@mui/material";
import axios from "axios";
import { authenticationService } from "src/services";
import { multy_put } from 'src/services/httpClient.service';

const APIURL = process.env.REACT_APP_BASE_API_URL

const EditYoutubeDialog = ({ handleClose, app, fetchYoutubeData }) => {
  const {vendorId, franchiseId} = authenticationService.getVendorFranchiseIds();
  const [formData, setFormData] = useState({
    title: "",
    categoryName: "",
    videoUrl: "",
    image: "",
    priority: "",
    thumbnailFile: null,
    thumbnailImage: ""
  });
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: AlertColor;
    }>({
      open: false,
      message: "",
      severity: "success", 
    });

  useEffect(() => {
    if (app) {
      setFormData({
        title: app.title || app.appName || "",
        categoryName: app.categoryName || "",
        videoUrl: app.videoUrl || "",
        image: app.image || "",
        priority: app.priority || "MEDIUM",
        thumbnailFile: null,
        thumbnailImage: app.thumbnailImage || app.image || ""
      });
    }
  }, [app]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, thumbnailFile: e.target.files[0] });
  };

  const handleSave = async () => {
  if (!app) return;
  
  // Validate required fields
  if (!formData.title || !formData.videoUrl || !formData.categoryName || !formData.priority) {
    setSnackbar({ 
      open: true, 
      message: "Please fill in all required fields.", 
      severity: "warning" 
    });
    return;
  }
  
  setLoading(true);
  try {
    const formDataToSend = new FormData();
    
    // Create the appData object with all the form fields
    const appData = {
      title: formData.title,
      categoryName: formData.categoryName,
      videoUrl: formData.videoUrl,
      image: formData.image || "",
      priority: formData.priority,
      vendorId: vendorId
    };
    
    // Append appData as a JSON string with the part name "appData"
    formDataToSend.append("appData", new Blob([JSON.stringify(appData)], { type: "application/json" }));
    
    // Append the thumbnail file if a new one is selected
    if (formData.thumbnailFile) {
      formDataToSend.append("thumbnailFile", formData.thumbnailFile);
    }

    await multy_put(`${APIURL}/api/youtube-apps/${app.id}`, formDataToSend);
    
    setSnackbar({ 
      open: true, 
      message: "YouTube app updated successfully!", 
      severity: "success" 
    });
    
    fetchYoutubeData();
    setTimeout(() => {
      handleClose();
    }, 2000);
   
  } catch (error) {
    console.error("Error updating YouTube app:", error);
    setSnackbar({ 
      open: true, 
      message: "Failed to update the app.", 
      severity: "error" 
    });
  } finally {
    setLoading(false);
  }
};

  return (
    <Box>
      <Grid container spacing={2} sx={{ p: 2, minWidth: "500px" }}>
        {/* Title Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Title *"
            name="title"
            fullWidth
            value={formData.title}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Category Name Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Category Name *"
            name="categoryName"
            fullWidth
            value={formData.categoryName}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Video URL Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Video URL *"
            name="videoUrl"
            fullWidth
            value={formData.videoUrl}
            onChange={handleChange}
            required
            variant="outlined"
          />
        </Grid>

        {/* Image URL Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Image URL"
            name="image"
            fullWidth
            value={formData.image}
            onChange={handleChange}
            variant="outlined"
            helperText="Optional: External image URL"
          />
        </Grid>

        {/* Priority Field */}
        <Grid item xs={12}>
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="priority-label">Priority *</InputLabel>
            <Select
              labelId="priority-label"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              label="Priority *"
              required
            >
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Thumbnail Preview and Upload */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {formData.thumbnailImage && (
              <Box>
                <p style={{ margin: '0 0 5px 0' }}>Current Thumbnail:</p>
                <img 
                  src={formData.thumbnailImage} 
                  alt="thumbnail" 
                  style={{ 
                    width: 100, 
                    height: 50, 
                    objectFit: 'cover',
                    borderRadius: 4,
                    border: '1px solid #ccc'
                  }} 
                />
              </Box>
            )}
            <Box>
              <Button variant="contained" component="label">
                Upload New Thumbnail
                <input
                  type="file"
                  accept="image/jpeg, image/jpg, image/png"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {formData.thumbnailFile && (
                <p style={{ margin: '5px 0 0 0', fontSize: '0.875rem', color: '#666' }}>
                  New file selected: {formData.thumbnailFile.name}
                </p>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    
      {/* Action Buttons */}
      <Box sx={{ margin: '20px', display: 'flex', flexDirection: "row-reverse", gap: 1 }}>
        <Button onClick={handleClose} color="primary" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained" 
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: '100' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditYoutubeDialog;